/**
 * liveTickAtoms.ts — Jotai atomic state for high-frequency live market ticks.
 *
 * WHY JOTAI INSTEAD OF ZUSTAND FOR LIVE TICKS?
 * ─────────────────────────────────────────────
 * Zustand's `set()` creates a new top-level state reference on every call.
 * When GlobalMarketEngine calls `updateTick()` every 2 seconds, ALL Zustand
 * subscribers (15+ components) re-render simultaneously — even if they only
 * care about a single field like `indexLtp`.
 *
 * Jotai atoms are independent. Writing to `indexLtpAtoms` only re-renders
 * the 2-3 components that subscribe to it. The other 12+ components remain
 * completely asleep. This eliminates the "re-render cascade" freeze.
 *
 * ARCHITECTURE:
 * - Zustand remains the source of truth for HISTORY, EXPIRY, and CONFIG.
 * - Jotai owns LIVE TICK DATA: LTP, meta, metrics, fullData.
 * - GlobalMarketEngine dual-writes to both (Zustand for persistence, Jotai for UI).
 */

import { atom } from 'jotai'
import type { MarketData } from '../types'

// ─── Per-Symbol Index LTP ────────────────────────────────────────────────────
export const indexLtpAtoms = atom<Record<string, number>>({})

// ─── Per-Symbol Market Meta (dayChange, vix, etc.) ───────────────────────────
export const marketMetaAtoms = atom<Record<string, {
    dayChange: number
    dayChangeP: number
    vix: number
    vixChange: number
    vixChangeP: number
    vixChangePercent?: number
    ltpChange?: number
    ltpChangePercent?: number
    timestamp?: number
}>>({})

// ─── Per-Symbol Market Metrics (support/resistance/shift — updated every 2s) ─
export const marketMetricsAtoms = atom<Record<string, {
    pcr: number
    oiVariance: number
    maxOiDiff: number
    minOiDiff: number
    maxStrike: number
    minStrike: number
    supportStrike: number
    supportStrike2: number
    resistanceStrike: number
    resistanceStrike2: number
    shift: number
    shiftOld: number
    ceCount: number
    peCount: number
    totalCount: number
    atmStrike: number
    ceOtmStrike: number
    peOtmStrike: number
    shiftV?: number
}>>({})

// ─── Per-Symbol Full MarketData (the complete options chain snapshot) ─────────
export const fullDataAtoms = atom<Record<string, MarketData>>({})

// ─── Derived: Single-symbol selectors (avoids map lookups in components) ─────

/** Read-only atom: returns the LTP for a specific symbol */
export function symbolLtpAtom(symbol: string) {
    return atom((get) => get(indexLtpAtoms)[symbol] ?? 0)
}

/** Read-only atom: returns the meta for a specific symbol */
export function symbolMetaAtom(symbol: string) {
    return atom((get) => get(marketMetaAtoms)[symbol] ?? null)
}

/** Read-only atom: returns the metrics for a specific symbol */
export function symbolMetricsAtom(symbol: string) {
    return atom((get) => get(marketMetricsAtoms)[symbol] ?? null)
}

/** Read-only atom: returns the full MarketData for a specific symbol */
export function symbolFullDataAtom(symbol: string) {
    return atom((get) => get(fullDataAtoms)[symbol] ?? null)
}

// ─── Imperative Setter (for use outside React, e.g. GlobalMarketEngine) ──────
// Jotai's `useSetAtom` can only be used inside components, but we also need
// to write from the GlobalMarketEngine's useEffect. The `store` API handles this.

import { createStore } from 'jotai'

/**
 * Global Jotai store instance — shared across the entire application.
 * This allows imperative reads/writes from non-component code (Workers, Effects).
 */
export const jotaiStore = createStore()

/**
 * Imperatively update all live tick atoms in a single batch.
 * Called from GlobalMarketEngine's useEffect after receiving a new Upstox tick.
 */
export function writeLiveTick(
    symbol: string,
    ltp: number,
    meta: any,
    metrics: any,
    fullData?: MarketData,
) {
    // Batch: write all atoms in the same microtask → single render pass
    jotaiStore.set(indexLtpAtoms, (prev) => {
        if (prev[symbol] === ltp) return prev // referential equality — no re-render
        return { ...prev, [symbol]: ltp }
    })

    if (meta) {
        jotaiStore.set(marketMetaAtoms, (prev) => {
            const existing = prev[symbol]
            if (existing &&
                existing.dayChange === meta.dayChange &&
                existing.dayChangeP === meta.dayChangeP &&
                existing.vix === meta.vix &&
                existing.vixChange === meta.vixChange
            ) return prev // unchanged — skip
            return { ...prev, [symbol]: meta }
        })
    }

    if (metrics) {
        jotaiStore.set(marketMetricsAtoms, (prev) => {
            const existing = prev[symbol]
            if (existing &&
                existing.pcr === metrics.pcr &&
                existing.atmStrike === metrics.atmStrike &&
                existing.shift === metrics.shift &&
                existing.supportStrike === metrics.supportStrike &&
                existing.resistanceStrike === metrics.resistanceStrike
            ) return prev // unchanged — skip
            return { ...prev, [symbol]: metrics }
        })
    }

    if (fullData) {
        jotaiStore.set(fullDataAtoms, (prev) => ({ ...prev, [symbol]: fullData }))
    }
}
